/**
 * FAQ Lumeska — smooth details/summary accordion.
 * Keeps native <details>/<summary> semantics (accessible, keyboard-operable,
 * works with zero JS). When Web Animations API is available and the visitor
 * has not requested reduced motion, clicks are intercepted to animate the
 * height smoothly instead of the native instant toggle.
 */
(function () {
  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class FaqAccordionItem {
    constructor(details, group) {
      this.details = details;
      this.group = group;
      this.summary = details.querySelector('.faq-lumeska__question');
      this.answer = details.querySelector('.faq-lumeska__answer');
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;

      if (!this.summary || !this.answer || !details.animate) return;

      this.summary.addEventListener('click', (event) => this.onClick(event));
    }

    onClick(event) {
      event.preventDefault();
      this.details.style.overflow = 'hidden';

      if (this.isClosing || !this.details.open) {
        this.open();
      } else if (this.isExpanding || this.details.open) {
        this.shrink();
      }
    }

    open() {
      if (this.group) this.group.closeOthers(this);

      this.details.style.height = `${this.details.offsetHeight}px`;
      this.details.open = true;
      window.requestAnimationFrame(() => this.expand());
    }

    shrink() {
      this.isClosing = true;
      const startHeight = `${this.details.offsetHeight}px`;
      const endHeight = `${this.summary.offsetHeight}px`;

      if (this.animation) this.animation.cancel();
      this.animation = this.details.animate(
        { height: [startHeight, endHeight] },
        { duration: 300, easing: 'ease-out' }
      );
      this.animation.onfinish = () => this.onAnimationFinish(false);
      this.animation.oncancel = () => {
        this.isClosing = false;
      };
    }

    expand() {
      this.isExpanding = true;
      const startHeight = `${this.details.offsetHeight}px`;
      const endHeight = `${this.summary.offsetHeight + this.answer.offsetHeight}px`;

      if (this.animation) this.animation.cancel();
      this.animation = this.details.animate(
        { height: [startHeight, endHeight] },
        { duration: 300, easing: 'ease-out' }
      );
      this.animation.onfinish = () => this.onAnimationFinish(true);
      this.animation.oncancel = () => {
        this.isExpanding = false;
      };
    }

    onAnimationFinish(open) {
      this.details.open = open;
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
      this.details.style.height = '';
      this.details.style.overflow = '';
    }

    closeImmediately() {
      if (!this.details.open) return;
      if (this.animation) this.animation.cancel();
      this.shrink();
    }
  }

  class FaqAccordionGroup {
    constructor(root) {
      this.singleOpen = root.dataset.singleOpen === 'true';
      this.items = Array.from(root.querySelectorAll('.faq-lumeska__item')).map(
        (details) => new FaqAccordionItem(details, this.singleOpen ? this : null)
      );
    }

    closeOthers(current) {
      this.items.forEach((item) => {
        if (item !== current) item.closeImmediately();
      });
    }
  }

  function initAll(root = document) {
    if (prefersReducedMotion()) return;
    root.querySelectorAll('.faq-lumeska__list:not([data-initialized])').forEach((el) => {
      el.setAttribute('data-initialized', 'true');
      new FaqAccordionGroup(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  }
})();
